import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createApi } from 'unsplash-js';
import classNames from 'classnames';
import { Waypoint } from 'react-waypoint';
import _ from 'lodash';
import styles from './UnsplashPicker.module.scss';
import Button from './Button';
import Form, { Field } from './Form';
import LoadingIndicator from './LoadingIndicator';
import StickyHeader from './StickyHeader';
import SVGIcon from './SVGIcon';
import UnsplashColorPicker from './UnsplashColorPicker';
import type { SupportedColorId } from './UnsplashColors';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import type { DisplayMode } from './BannerImageList';
import BannerImageList, {
  BannerImageListButton,
  BannerImageSkeleton,
} from './BannerImageList';
import notFound from '../images/empty/not-found.svg';
import authTokenUtil from '../account/authTokenUtil';

type Photos = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof createApi>['search']['getPhotos']>
  >['response']
>;

const getUnsplashApi = _.memoize(() =>
  createApi({
    apiUrl: `https://${window.__slab.ORG_HOST}/unsplash`,
    headers: {
      Authorization: authTokenUtil.get() ?? '',
    },
  }),
);

const DEFAULT_PHOTOS: Photos = {
  total: 0,
  total_pages: 0,
  results: [],
};

const TOPIC_SLUGS = [
  'wallpapers',
  'travel',
  'nature',
  'textures-patterns',
  '3d-renders',
] as const;

function useUnsplashPhotoSearch({
  query,
  colorId,
  perPage,
}: {
  query: string;
  colorId?: SupportedColorId;
  perPage: number;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [photos, setPhotos] = useState<Photos>(DEFAULT_PHOTOS);
  const currentPage = Math.ceil(photos.results.length / perPage);
  const hasMore = photos.total_pages > currentPage;

  const topicSlug = useMemo(() => {
    const slug = _.sample(TOPIC_SLUGS);
    if (slug == null) throw new Error("Topic slug can't be null");
    return slug;
  }, []);

  const loadPage = useCallback(
    async (page: number, color: SupportedColorId | undefined) => {
      setIsLoading(true);
      try {
        if (query !== '') {
          return await getUnsplashApi().search.getPhotos({
            query,
            orientation: 'landscape',
            color,
            perPage,
            page,
          });
        }

        const results = await getUnsplashApi().topics.getPhotos({
          topicIdOrSlug: topicSlug,
          perPage,
          page,
          orientation: 'landscape',
        });

        if (results.response) {
          return {
            response: {
              results: results.response.results,
              total: results.response.total,
              total_pages: results.response.total / perPage,
            },
          };
        }

        return { response: null };
      } catch (apiError) {
        setError(apiError);
        return { response: null };
      } finally {
        setIsLoading(false);
      }
    },
    [query, perPage, topicSlug],
  );

  const fetchMore = useCallback(async () => {
    if (hasMore) {
      await loadPage(currentPage + 1, colorId).then((result) => {
        if (result.response) {
          setPhotos((current) => ({
            ...current,
            results: [...current.results, ...result.response.results],
          }));
        }
      });
    }
  }, [loadPage, hasMore, currentPage, colorId]);

  useEffect(() => {
    setPhotos(DEFAULT_PHOTOS);
    loadPage(1, colorId).then((result) => {
      if (result.response) {
        setPhotos(result.response);
      }
    });
  }, [loadPage, colorId]);

  return { results: photos.results, fetchMore, hasMore, isLoading, error };
}

function scaledUrl(rawUrl: string, width: number) {
  const url = new URL(rawUrl);
  url.search = new URLSearchParams({
    w: `${width}`,
  }).toString();
  return url.toString();
}

interface UnsplashPickerProps {
  displayMode: DisplayMode;
  onPick(banner: { image: string; unsplashId: string }): void;
}

const UnsplashPicker = ({ displayMode, onPick }: UnsplashPickerProps) => {
  const [query, setQuery] = useState('');
  const [colorId, setColorId] = useState<SupportedColorId | null>(null);
  const [fetchingImageId, setFetchingImageId] = useState<string | null>(null);
  const imagesPerPage = displayMode === 'grid' ? 15 : 10;
  const { results, hasMore, fetchMore, isLoading, error } =
    useUnsplashPhotoSearch({
      query,
      colorId: query === '' ? undefined : colorId ?? undefined,
      perPage: imagesPerPage,
    });

  const isShowingEmptyState = results.length === 0 && !isLoading;

  return (
    <div
      className={classNames(styles.container, {
        [styles.isShowingEmptyState]: isShowingEmptyState,
      })}
    >
      <StickyHeader topOffset={60}>
        <div className={styles.searchFormContainer}>
          <Form<{ query: string }>
            initialValues={{ query }}
            onSubmit={(values) => setQuery(values.query)}
          >
            <Field
              autoFocus
              name="query"
              placeholder="Search for an image"
              type="text"
              margin="none"
              prepend={
                <SVGIcon className={styles.inputPrepend} size={16}>
                  {searchIcon}
                </SVGIcon>
              }
            />
          </Form>
          <UnsplashColorPicker onChange={setColorId} value={colorId} />
        </div>
      </StickyHeader>
      {isShowingEmptyState ? (
        <div className={styles.emptyStateContainer}>
          <img src={notFound} alt="" />
          <div>No results found</div>
        </div>
      ) : (
        <div className={styles.resultsContainer}>
          {results.length === 0 && isLoading ? (
            <BannerImageList displayMode={displayMode}>
              {_.range(imagesPerPage).map((i) => (
                <BannerImageSkeleton key={i} />
              ))}
            </BannerImageList>
          ) : (
            <>
              <BannerImageList displayMode={displayMode}>
                {results.map(
                  ({
                    id,
                    urls,
                    alt_description: alt,
                    links: { download_location: downloadLocation },
                    user: { name, links },
                  }) => {
                    const isLoadingImage = id === fetchingImageId;
                    const profileLink = new URL(links.html);
                    profileLink.search = new URLSearchParams({
                      utm_source: 'slab',
                      utm_medium: 'referral',
                    }).toString();

                    return (
                      <div
                        key={id}
                        className={classNames(styles.imageContainer, {
                          [styles.isLoading]: isLoadingImage,
                        })}
                      >
                        <BannerImageListButton
                          onClick={async () => {
                            setFetchingImageId(id);

                            const url = scaledUrl(urls.raw, 2880);
                            await fetch(url);
                            await getUnsplashApi().photos.trackDownload({
                              downloadLocation,
                            });
                            setFetchingImageId(null);

                            onPick({
                              image: url,
                              unsplashId: id,
                            });
                          }}
                          src={urls.regular}
                          alt={alt ?? 'Unsplash Image'}
                        />
                        <div className={styles.imageOverlay}>
                          {isLoadingImage ? (
                            <LoadingIndicator delay={0} />
                          ) : (
                            <div className={styles.authorContainer}>
                              <a
                                href={profileLink.toString()}
                                target="_blank"
                                rel="noreferrer noopener"
                              >
                                {name}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </BannerImageList>

              {hasMore && !isLoading && (
                <>
                  {error == null ? (
                    <Waypoint onEnter={fetchMore} />
                  ) : (
                    <div className={styles.loader}>
                      <Button block variant="link" onClick={fetchMore}>
                        Load more
                      </Button>
                    </div>
                  )}
                </>
              )}

              {isLoading && (
                <div className={styles.loader}>
                  <LoadingIndicator delay={0} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UnsplashPicker;
